import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../services/apiService'
import { api } from '../services/authService'
import {
  PlusIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  PencilIcon,
  DocumentCheckIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  Squares2X2Icon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  XCircleIcon,
  EllipsisVerticalIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import ContractDetailModal from '../components/ContractDetailModal'
import SignaturesModal from '../components/contracts/SignaturesModal'
import CancelContractModal from '../components/contracts/CancelContractModal'
import { formatCurrency } from '../utils/formatters'

// Parse date string sem shift de timezone (datas vêm como 'YYYY-MM-DD' do backend)
const fmtDate = (d) => {
  if (!d) return '—'
  const [y, m, day] = String(d).slice(0, 10).split('-')
  return `${day}/${m}/${y}`
}

const ContractsPage = () => {
  const navigate = useNavigate()
  const [contracts, setContracts] = useState({
    negotiating: [],
    approved: [],
    sent_to_clicksign: [],
    awaiting_delivery_term: [],
    awaiting_delivery_inspection: [],
    active: [],
    awaiting_return_term: [],
    awaiting_return_inspection: [],
    completed: [],
    cancelled: []
  })
  const [loading, setLoading] = useState(true)
  const [selectedContract, setSelectedContract] = useState(null)
  const [viewMode, setViewMode] = useState('list') // 'kanban' or 'list'
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'negotiating', 'approved', 'active', 'completed', 'cancelled'
  const [signaturesModalOpen, setSignaturesModalOpen] = useState(false)
  const [selectedContractIdForSignatures, setSelectedContractIdForSignatures] = useState(null)
  const [signaturesModalType, setSignaturesModalType] = useState('contract')
  const [editingDueDay, setEditingDueDay] = useState(null) // contract id being edited
  const [newDueDay, setNewDueDay] = useState('')
  const [cancelModalContract, setCancelModalContract] = useState(null)
  const [openActionMenu, setOpenActionMenu] = useState(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 })
  const [renewModal, setRenewModal] = useState(null) // contract being renewed
  const [renewForm, setRenewForm] = useState({ mode: 'months', months: 12, days: 30, custom_date: '', new_monthly_rate: '' })
  const [renewLoading, setRenewLoading] = useState(false)
  const [resendClicksignModal, setResendClicksignModal] = useState(null) // contract being resent
  const [resendLoading, setResendLoading] = useState(false)
  const [termModal, setTermModal] = useState(null)
  const [termForm, setTermForm] = useState({
    representative_name: '', representative_cpf: '', representative_rg: '',
    representative_cnh: '', representative_cnh_category: '', representative_cnh_validity: '',
    representative_phone: '', representative_role: ''
  })
  const [termLoading, setTermLoading] = useState(false)
  const [returnTermModal, setReturnTermModal] = useState(null)
  const [returnTermForm, setReturnTermForm] = useState({
    representative_name: '', representative_cpf: '', representative_rg: '',
    representative_cnh: '', representative_cnh_category: '', representative_cnh_validity: '',
    representative_phone: '', representative_role: ''
  })
  const [returnTermLoading, setReturnTermLoading] = useState(false)


  useEffect(() => {
    loadContracts()
  }, [])

  const loadContracts = async () => {
  setLoading(true)
  try {
    const response = await api.get('/api/contracts')
    const result = response.data
    const contractList = result.data || []

    // Organizar contratos por status
    const organized = {
      negotiating: contractList.filter(c => c.status === 'negotiating'),
      approved: contractList.filter(c => c.status === 'approved'),
      sent_to_clicksign: contractList.filter(c => c.status === 'sent_to_clicksign'),
      awaiting_delivery_term: contractList.filter(c => c.status === 'awaiting_delivery_term' || c.status === 'awaiting_term'),
      awaiting_delivery_inspection: contractList.filter(c => c.status === 'awaiting_delivery_inspection'),
      active: contractList.filter(c => c.status === 'active'),
      awaiting_return_term: contractList.filter(c => c.status === 'awaiting_return_term'),
      awaiting_return_inspection: contractList.filter(c => c.status === 'awaiting_return_inspection'),
      completed: contractList.filter(c => c.status === 'finished' || c.status === 'completed'),
      cancelled: contractList.filter(c => c.status === 'cancelled')
    }

    setContracts(organized)
  } catch (error) {
    console.error('Erro ao carregar contratos:', error)
    if (error.response?.status === 401) {
      toast.error('Sessão expirada. Faça login novamente.')
    } else {
      toast.error('Erro ao carregar contratos')
    }
  } finally {
    setLoading(false)
  }
}

  const getVehicleIcon = (type) => {
    const icons = {
      excavator: '🏗️',
      truck: '🚛',
      tractor: '🚜',
      crane: '🏗️',
      loader: '🚧',
      bulldozer: '🚜'
    }
    return icons[type] || '🚛'
  }

  const getStatusColor = (status) => {
    const colors = {
      negotiating: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
      approved: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
      sent_to_clicksign: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
      awaiting_delivery_term: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
      awaiting_term: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
      awaiting_delivery_inspection: 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20',
      active: 'border-green-500 bg-green-50 dark:bg-green-900/20',
      awaiting_return_term: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20',
      awaiting_return_inspection: 'border-teal-500 bg-teal-50 dark:bg-teal-900/20',
      completed: 'border-gray-500 bg-gray-50 dark:bg-gray-900/20',
      cancelled: 'border-red-500 bg-red-50 dark:bg-red-900/20'
    }
    return colors[status] || colors.negotiating
  }

  const getStatusTitle = (status) => {
    const titles = {
      negotiating: '🔄 Em Negociação',
      approved: '✅ Aprovados',
      sent_to_clicksign: '📝 Enviado para Assinatura',
      awaiting_delivery_term: '📋 Aguard. Termo Entrega',
      awaiting_term: '📋 Aguard. Termo Entrega',
      awaiting_delivery_inspection: '🔍 Aguard. Vistoria Entrega',
      active: '🚀 Ativos',
      awaiting_return_term: '📋 Aguard. Termo Devolução',
      awaiting_return_inspection: '🔍 Aguard. Vistoria Devolução',
      completed: '✅ Concluídos',
      cancelled: '❌ Cancelados'
    }
    return titles[status]
  }

  const handleCreateContract = () => {
    navigate('/contracts/new')
  }

  const handleGenerateReport = () => {
    toast.success('Gerando relatório de contratos...')
  }

  const handleViewContract = (id) => {
    const contractData = Object.values(contracts).flat().find(c => c.id === id)
    setSelectedContract(contractData)
  }

  const handleEditContract = (id) => {
    navigate(`/contracts/${id}/edit`)
  }

  const handleSignContract = (id) => {
    toast.success(`Enviando contrato ID: ${id} para assinatura via DocuSign`)
  }

  const handleViewPayments = (id) => {
    navigate(`/invoices?contract=${id}`)
    toast(`Visualizando pagamentos do contrato ID: ${id}`, {
      icon: 'ℹ️',
    })
  }

  const handleDownloadClicksign = async (contract) => {
    try {
      toast.loading('Baixando documento do Clicksign...', { id: 'cs-download' })
      const response = await api.get(`/api/clicksign/documents/${contract.clicksign_document_key}/download`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `contrato_${contract.contract_number}_clicksign.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success('Download concluído!', { id: 'cs-download' })
    } catch (error) {
      console.error('Erro ao baixar documento:', error)
      toast.error('Erro ao baixar documento do Clicksign', { id: 'cs-download' })
    }
  }

  const handleSendTerm = async (contract) => {
    setTermLoading(true)
    const toastId = toast.loading('Gerando e enviando termo de entrega...')
    try {
      await api.post(`/api/authorization-terms/${contract.id}/send-to-clicksign`, {
        termData: termForm
      })
      toast.success('Termo enviado para assinatura!', { id: toastId })
      setTermModal(null)
      setTermForm({
        representative_name: '', representative_cpf: '', representative_rg: '',
        representative_cnh: '', representative_cnh_category: '', representative_cnh_validity: '',
        representative_phone: '', representative_role: ''
      })
      loadContracts()
    } catch (error) {
      console.error('Erro ao enviar termo:', error)
      toast.error(error.response?.data?.error || 'Erro ao enviar termo', { id: toastId })
    } finally {
      setTermLoading(false)
    }
  }

  const handleDownloadTerm = async (contract) => {
    const toastId = toast.loading('Baixando PDF do termo...')
    try {
      const response = await api.get(`/api/authorization-terms/${contract.id}/download-pdf`)
      const { pdfBase64 } = response.data
      const byteCharacters = atob(pdfBase64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `termo_autorizacao_${contract.contract_number || contract.id}.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success('Download concluído!', { id: toastId })
    } catch (error) {
      console.error('Erro ao baixar termo:', error)
      toast.error(error.response?.data?.error || 'Erro ao baixar PDF do termo', { id: toastId })
    }
  }

  const handleSendReturnTerm = async (contract) => {
    setReturnTermLoading(true)
    const toastId = toast.loading('Gerando e enviando termo de devolução...')
    try {
      await api.post(`/api/return-terms/${contract.id}/send-to-clicksign`, {
        termData: returnTermForm
      })
      toast.success('Termo de devolução enviado para assinatura!', { id: toastId })
      setReturnTermModal(null)
      setReturnTermForm({
        representative_name: '', representative_cpf: '', representative_rg: '',
        representative_cnh: '', representative_cnh_category: '', representative_cnh_validity: '',
        representative_phone: '', representative_role: ''
      })
      loadContracts()
    } catch (error) {
      console.error('Erro ao enviar termo de devolução:', error)
      toast.error(error.response?.data?.error || 'Erro ao enviar termo de devolução', { id: toastId })
    } finally {
      setReturnTermLoading(false)
    }
  }

  const handleDownloadReturnTerm = async (contract) => {
    const toastId = toast.loading('Baixando PDF do termo de devolução...')
    try {
      const response = await api.get(`/api/return-terms/${contract.id}/download-pdf`)
      const { pdfBase64 } = response.data
      const byteCharacters = atob(pdfBase64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `termo_devolucao_${contract.contract_number || contract.id}.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success('Download concluído!', { id: toastId })
    } catch (error) {
      console.error('Erro ao baixar termo de devolução:', error)
      toast.error(error.response?.data?.error || 'Erro ao baixar PDF do termo de devolução', { id: toastId })
    }
  }

  const handleResendClicksign = async (contract) => {
    setResendLoading(true)
    const toastId = toast.loading('Cancelando envelope anterior e gerando novo...')
    try {
      // Buscar signatários atuais do contrato
      const sigRes = await api.get(`/api/contracts/${contract.id}/signatures`)
      const currentSigners = (sigRes.data.signatures || []).map(s => ({
        name: s.signer_name,
        email: s.signer_email,
        role: s.signer_role,
        sign_as: ['locador', 'locatario'].includes(s.signer_role) ? 'party' : 'witness'
      }))

      if (currentSigners.length === 0) {
        toast.error('Nenhum signatário encontrado. Acesse o editor do contrato para reenviar.', { id: toastId })
        setResendClicksignModal(null)
        return
      }

      await api.post(`/api/contracts/${contract.id}/resend-to-clicksign`, {
        signers: currentSigners,
        deadline: contract.clicksign_deadline
      })

      toast.success('Novo envelope enviado! Todos os signatários receberão o link por email.', { id: toastId })
      setResendClicksignModal(null)
      loadContracts()
    } catch (error) {
      console.error('Erro ao reenviar para Clicksign:', error)
      toast.error(error.response?.data?.error || 'Erro ao reenviar para Clicksign', { id: toastId })
    } finally {
      setResendLoading(false)
    }
  }

  const handleEditFromModal = (contract) => {
    setSelectedContract(null)
    navigate(`/contracts/${contract.id}/edit`)
  }



  const handleDeleteContract = (contract) => {
    setCancelModalContract(contract)
  }

  const handleRepossessVehicle = async (contract) => {
    const confirmed = window.confirm(
      `RETOMAR VEÍCULO - Contrato ${contract.contract_number}\n\n` +
      `Cliente: ${contract.client_name}\n` +
      `Veículo: ${contract.vehicle_info || contract.vehicle_id}\n\n` +
      `O veículo será liberado para novo aluguel.\n` +
      `O contrato e as faturas/cobranças continuarão ATIVOS para fins de judicialização.\n\n` +
      `Confirma a retomada do veículo?`
    )
    if (!confirmed) return

    const notes = window.prompt('Observação (opcional):') || ''
    const toastId = toast.loading('Retomando veículo...')
    try {
      const response = await api.post(`/api/contracts/${contract.id}/repossess`, { notes })
      toast.success(response.data.message, { id: toastId, duration: 5000 })
      loadContracts()
    } catch (error) {
      console.error('Erro ao retomar veículo:', error)
      toast.error(error.response?.data?.error || 'Erro ao retomar veículo', { id: toastId })
    }
  }

  const handleConfirmCancelContract = async (contract, invoiceIdsToCancel) => {
    const toastId = toast.loading('Cancelando contrato...')
    try {
      const response = await api.delete(`/api/contracts/${contract.id}`, {
        data: { invoiceIdsToCancel }
      })
      const details = response.data.details || {}
      let msg = 'Contrato cancelado!'
      if (details.cancelledInvoices > 0) msg += ` ${details.cancelledInvoices} fatura(s) cancelada(s).`
      if (details.cancelledBoletos > 0) msg += ` ${details.cancelledBoletos} boleto(s) cancelado(s) no Asaas.`
      toast.success(msg, { id: toastId, duration: 5000 })
      setCancelModalContract(null)
      loadContracts()
    } catch (error) {
      console.error('Erro ao cancelar contrato:', error)
      toast.error(error.response?.data?.error || 'Erro ao cancelar contrato', { id: toastId })
    }
  }

  const handleUpdateDueDay = async (contractId) => {
    try {
      await apiService.updateContractDueDay(contractId, parseInt(newDueDay))
      toast.success(`Vencimento atualizado para dia ${newDueDay}`)
      setEditingDueDay(null)
      loadContracts()
    } catch (error) {
      toast.error('Erro ao atualizar: ' + (error.response?.data?.error || error.message))
    }
  }

  const handleRenewContract = async () => {
    if (!renewModal) return
    setRenewLoading(true)
    try {
      const payload = {}
      if (renewForm.mode === 'months') payload.extension_months = parseInt(renewForm.months)
      else if (renewForm.mode === 'days') payload.extension_days = parseInt(renewForm.days)
      else payload.new_end_date = renewForm.custom_date
      if (renewForm.new_monthly_rate) payload.new_monthly_rate = parseFloat(renewForm.new_monthly_rate)

      const result = await apiService.renewContract(renewModal.id, payload)
      toast.success(result.message)
      setRenewModal(null)
      loadContracts()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao prorrogar contrato')
    } finally {
      setRenewLoading(false)
    }
  }

  const getRenewPreviewDate = () => {
    if (!renewModal) return null
    try {
      // Parse sem timezone shift: tratar como data local
      const [y, m, day] = String(renewModal.end_date).slice(0, 10).split('-').map(Number)
      const base = new Date(y, m - 1, day)
      if (renewForm.mode === 'months') {
        const d = new Date(base); d.setMonth(d.getMonth() + parseInt(renewForm.months || 0)); return d
      } else if (renewForm.mode === 'days') {
        const d = new Date(base); d.setDate(d.getDate() + parseInt(renewForm.days || 0)); return d
      } else if (renewForm.custom_date) {
        const [cy, cm, cd] = renewForm.custom_date.split('-').map(Number)
        return new Date(cy, cm - 1, cd)
      }
    } catch { return null }
    return null
  }

  // Função para filtrar contratos
  const getFilteredContracts = () => {
    let allContracts = Object.values(contracts).flat()

    // Filtrar por status
    if (statusFilter !== 'all') {
      allContracts = allContracts.filter(c => c.status === statusFilter)
    }

    // Filtrar por busca
    if (searchTerm) {
      allContracts = allContracts.filter(c =>
        c.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.vehicle_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contract_identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.id?.toString().includes(searchTerm)
      )
    }

    return allContracts
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando contratos...</p>
        </div>
      </div>
    )
  }

  const totalContracts = Object.values(contracts).reduce((sum, arr) => sum + arr.length, 0)
  const activeContracts = contracts.active.length

  // Valor total máximo dos contratos ativos (soma dos total_value)
  const activeTotalValue = contracts.active.reduce((sum, c) => sum + (parseFloat(c.total_value || c.total_amount) || 0), 0)

  // Valor remanescente dos contratos ativos (meses restantes × mensalidade)
  const remainingValue = contracts.active.reduce((sum, c) => {
    const days = parseFloat(c.days_remaining) || 0
    const months = Math.max(0, days / 30)
    return sum + months * (parseFloat(c.monthly_rate) || 0)
  }, 0)

  // Mensalidade média dos contratos ativos
  const avgMonthlyRate = activeContracts > 0
    ? contracts.active.reduce((sum, c) => sum + (parseFloat(c.monthly_rate) || 0), 0) / activeContracts
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            📋 Gestão de Contratos
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Contratos organizados por estágio com integração DocuSign
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleGenerateReport}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            <span>Relatório</span>
          </button>
          <button
            onClick={handleCreateContract}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Novo Contrato</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex-shrink-0 text-xl sm:text-2xl">📋</div>
            <div className="ml-3 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{totalContracts}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-green-50 dark:bg-green-900/20 flex-shrink-0 text-xl sm:text-2xl">🚀</div>
            <div className="ml-3 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Ativos</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{activeContracts}</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{formatCurrency(contracts.active.reduce((s, c) => s + (parseFloat(c.monthly_rate) || 0), 0))}/mês</p>
            </div>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex-shrink-0 text-xl sm:text-2xl">💰</div>
            <div className="ml-3 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Remanescente</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-600 truncate">{formatCurrency(remainingValue)}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 flex-shrink-0 text-xl sm:text-2xl">📊</div>
            <div className="ml-3 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Mensalidade Média</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-600 truncate">{formatCurrency(avgMonthlyRate)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Visual - Sempre visível */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Pipeline de Contratos</h3>
          <span className="text-xs text-gray-500">{totalContracts} total</span>
        </div>
        <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex items-center space-x-2 min-w-[480px]">
          {[
            { key: 'negotiating', label: 'Negociação', color: 'bg-yellow-500' },
            { key: 'approved', label: 'Aprovado', color: 'bg-blue-500' },
            { key: 'sent_to_clicksign', label: 'Assinatura', color: 'bg-purple-500' },
            { key: 'awaiting_delivery_term', label: 'T. Entrega', color: 'bg-orange-500' },
            { key: 'awaiting_delivery_inspection', label: 'V. Entrega', color: 'bg-cyan-500' },
            { key: 'active', label: 'Ativo', color: 'bg-green-500' },
            { key: 'awaiting_return_term', label: 'T. Devol.', color: 'bg-amber-500' },
            { key: 'awaiting_return_inspection', label: 'V. Devol.', color: 'bg-teal-500' },
            { key: 'completed', label: 'Concluído', color: 'bg-gray-500' },
            { key: 'cancelled', label: 'Cancelado', color: 'bg-red-500' }
          ].map((stage, index) => {
            const count = contracts[stage.key]?.length || 0
            const percentage = totalContracts > 0 ? (count / totalContracts) * 100 : 0

            return (
              <React.Fragment key={stage.key}>
                <div className="flex-1">
                  <div
                    className="flex items-center justify-between mb-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setStatusFilter(stage.key)}
                  >
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{stage.label}</span>
                    <span className="text-xs text-gray-500">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`${stage.color} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                      title={`${percentage.toFixed(1)}%`}
                    />
                  </div>
                </div>
                {index < 5 && (
                  <div className="text-gray-400 pb-4">→</div>
                )}
              </React.Fragment>
            )
          })}
        </div>
        </div>
      </div>

      {/* Controles de Visualização e Filtros */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* Toggle de Visualização */}
        <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-700 text-blue-600 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <ListBulletIcon className="w-4 h-4" />
            <span className="text-sm font-medium">Lista</span>
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
              viewMode === 'kanban'
                ? 'bg-white dark:bg-gray-700 text-blue-600 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <Squares2X2Icon className="w-4 h-4" />
            <span className="text-sm font-medium">Kanban</span>
          </button>
        </div>

        {/* Busca e Filtros */}
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          {/* Busca */}
          <div className="relative flex-1 sm:flex-none">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente, veículo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Filtro de Status */}
          <div className="relative">
            <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-9 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white appearance-none cursor-pointer"
            >
              <option value="all">Todos Status</option>
              <option value="negotiating">Em Negociação</option>
              <option value="approved">Aprovados</option>
              <option value="sent_to_clicksign">Enviado p/ Assinatura</option>
              <option value="awaiting_delivery_term">Aguardando Termo Entrega</option>
              <option value="awaiting_delivery_inspection">Aguardando Vistoria Entrega</option>
              <option value="active">Ativos</option>
              <option value="awaiting_return_term">Aguardando Termo Devolução</option>
              <option value="awaiting_return_inspection">Aguardando Vistoria Devolução</option>
              <option value="completed">Concluídos</option>
              <option value="cancelled">Cancelados</option>
            </select>
          </div>
        </div>
      </div>

      {/* Visualização Condicional: Lista ou Kanban */}
      {viewMode === 'list' ? (
        /* VISUALIZAÇÃO DE LISTA/TABELA */
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Identificação
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Cliente / Veículo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Assinaturas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Vigência
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {getFilteredContracts().length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center">
                      <div className="text-gray-500 dark:text-gray-400">
                        <div className="text-4xl mb-2">📋</div>
                        <p>Nenhum contrato encontrado</p>
                        {(searchTerm || statusFilter !== 'all') && (
                          <button
                            onClick={() => {
                              setSearchTerm('')
                              setStatusFilter('all')
                            }}
                            className="text-sm text-blue-600 hover:underline mt-2"
                          >
                            Limpar filtros
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  getFilteredContracts().map((contract) => (
                    <tr
                      key={contract.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="text-sm font-mono font-semibold text-gray-900 dark:text-white">
                          {contract.contract_identifier || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{getVehicleIcon(contract.vehicle_type)}</span>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              {contract.client_name}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {contract.vehicle_brand} {contract.vehicle_model} - {contract.license_plate}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          contract.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                          contract.status === 'negotiating' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          contract.status === 'approved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                          contract.status === 'sent_to_clicksign' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                          contract.status === 'awaiting_delivery_term' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                          contract.status === 'awaiting_delivery_inspection' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400' :
                          contract.status === 'awaiting_return_term' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                          contract.status === 'awaiting_return_inspection' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' :
                          contract.status === 'completed' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' :
                          contract.status === 'awaiting_term' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {getStatusTitle(contract.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-center justify-center gap-1">
                          {contract.total_signatures > 0 ? (
                            <button
                              onClick={() => {
                                setSelectedContractIdForSignatures(contract.id)
                                setSignaturesModalType('contract')
                                setSignaturesModalOpen(true)
                              }}
                              className="inline-flex items-center justify-center space-x-1 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors group"
                            >
                              <span className={`text-sm font-semibold ${
                                contract.signed_signatures === contract.total_signatures
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-purple-600 dark:text-purple-400'
                              }`}>
                                {contract.signed_signatures}/{contract.total_signatures}
                              </span>
                              {contract.signed_signatures === contract.total_signatures ? (
                                <span className="text-green-500">✓</span>
                              ) : (
                                <span className="text-orange-500">⏳</span>
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                          {contract.term_status && (
                            <button
                              onClick={() => {
                                setSelectedContractIdForSignatures(contract.id)
                                setSignaturesModalType('delivery')
                                setSignaturesModalOpen(true)
                              }}
                              className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity ${
                              contract.term_status === 'signed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              contract.term_status === 'sent_to_clicksign' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                              'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {contract.term_status === 'signed' ? 'Entrega ✓' :
                               contract.term_status === 'sent_to_clicksign' ? `Entrega ${contract.term_signed_count || 0}/${contract.term_total_count || 0}` :
                               'Entrega pendente'}
                            </button>
                          )}
                          {contract.return_term_status && (
                            <button
                              onClick={() => {
                                setSelectedContractIdForSignatures(contract.id)
                                setSignaturesModalType('return')
                                setSignaturesModalOpen(true)
                              }}
                              className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity ${
                              contract.return_term_status === 'signed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              contract.return_term_status === 'sent_to_clicksign' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {contract.return_term_status === 'signed' ? 'Devol. ✓' :
                               contract.return_term_status === 'sent_to_clicksign' ? `Devol. ${contract.return_term_signed_count || 0}/${contract.return_term_total_count || 0}` :
                               'Devol. pendente'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {fmtDate(contract.start_date)}
                        </div>
                        <div className="text-xs text-gray-500">
                          até {fmtDate(contract.end_date)}
                        </div>
                        {['active', 'negotiating', 'approved', 'sent_to_clicksign', 'awaiting_term'].includes(contract.status) && contract.days_remaining !== null && contract.days_remaining !== undefined && (() => {
                          const days = parseInt(contract.days_remaining)
                          if (days <= 0) return (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">Vencido</span>
                          )
                          if (days < 35) return (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">{days}d restantes</span>
                          )
                          return <div className="text-xs font-medium mt-0.5 text-gray-400">{days}d restantes</div>
                        })()}
                        {contract.renewal_count > 0 && (
                          <div className="text-xs text-indigo-500 font-medium mt-0.5">🔄 Renovado {contract.renewal_count}x</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {contract.status === 'active' && parseFloat(contract.days_remaining) > 0 ? (
                          <>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(Math.max(0, parseFloat(contract.days_remaining) / 30) * parseFloat(contract.monthly_rate || 0))}
                            </div>
                            <div className="text-xs text-gray-400">
                              máx. {formatCurrency(contract.total_value || contract.total_amount)}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(contract.total_value || contract.total_amount)}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-0.5">
                          {formatCurrency(contract.monthly_rate)}/mês
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const menuHeight = 360;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const top = spaceBelow >= menuHeight ? rect.bottom + 4 : rect.top - menuHeight - 4;
                            setMenuPosition({ top: Math.max(8, top), right: window.innerWidth - rect.right });
                            setOpenActionMenu(openActionMenu === contract.id ? null : contract.id);
                          }}
                          className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Ações"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>
                        {openActionMenu === contract.id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setOpenActionMenu(null)} />
                            <div className="fixed w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1 text-left"
                              style={{ top: menuPosition.top, right: menuPosition.right }}>
                              <button
                                onClick={() => { setOpenActionMenu(null); handleViewContract(contract.id) }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <EyeIcon className="w-4 h-4 text-blue-500" /> Visualizar
                              </button>
                              <button
                                onClick={() => { setOpenActionMenu(null); handleEditContract(contract.id) }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <PencilIcon className="w-4 h-4 text-yellow-500" /> Editar
                              </button>
                              {contract.status === 'active' && (
                                <>
                                  <button
                                    onClick={() => { setOpenActionMenu(null); navigate(`/contracts/${contract.id}/invoices`) }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <CalendarDaysIcon className="w-4 h-4 text-indigo-500" /> Faturas ({contract.total_invoices || 0}/{contract.expected_months || '—'})
                                  </button>
                                  <button
                                    onClick={() => { setOpenActionMenu(null); handleViewPayments(contract.id) }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <CurrencyDollarIcon className="w-4 h-4 text-green-500" /> Pagamentos
                                  </button>
                                  {editingDueDay === contract.id ? (
                                    <div className="flex items-center gap-1 px-4 py-2">
                                      <select
                                        value={newDueDay}
                                        onChange={(e) => setNewDueDay(e.target.value)}
                                        className="w-16 px-1 py-1 border border-blue-400 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        autoFocus
                                      >
                                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                          <option key={d} value={d}>{d}</option>
                                        ))}
                                      </select>
                                      <button onClick={() => handleUpdateDueDay(contract.id)} className="p-1 text-green-600 hover:bg-green-50 rounded text-xs">✓</button>
                                      <button onClick={() => setEditingDueDay(null)} className="p-1 text-gray-400 hover:bg-gray-50 rounded text-xs">✕</button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => { setEditingDueDay(contract.id); setNewDueDay(contract.dia_vencimento_boleto || 15) }}
                                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <PencilIcon className="w-4 h-4 text-blue-400" /> Venc. Dia {contract.dia_vencimento_boleto || 15}
                                    </button>
                                  )}
                                </>
                              )}
                              {['awaiting_delivery_term', 'awaiting_term'].includes(contract.status) && (
                                <button
                                  onClick={() => { setOpenActionMenu(null); setTermModal(contract) }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 font-medium"
                                >
                                  📋 Termo de Entrega
                                </button>
                              )}
                              {contract.term_status && (
                                <button
                                  onClick={() => { setOpenActionMenu(null); handleDownloadTerm(contract) }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  <DocumentArrowDownIcon className="w-4 h-4 text-orange-500" /> Baixar Termo Entrega
                                </button>
                              )}
                              {['active', 'awaiting_return_term', 'completed', 'finished'].includes(contract.status) && !contract.return_term_status && (
                                <button
                                  onClick={() => { setOpenActionMenu(null); setReturnTermModal(contract) }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 font-medium"
                                >
                                  <TruckIcon className="w-4 h-4" /> Termo de Devolução
                                </button>
                              )}
                              {contract.return_term_status && (
                                <>
                                  <button
                                    onClick={() => { setOpenActionMenu(null); handleDownloadReturnTerm(contract) }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <DocumentArrowDownIcon className="w-4 h-4 text-amber-500" /> Baixar Termo Devolução
                                  </button>
                                  {contract.return_term_status !== 'signed' && (
                                    <button
                                      onClick={() => { setOpenActionMenu(null); setReturnTermModal(contract) }}
                                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <TruckIcon className="w-4 h-4 text-amber-500" /> Reenviar Termo Devolução
                                    </button>
                                  )}
                                </>
                              )}
                              {!contract.clicksign_document_key && !['active', 'completed', 'finished', 'cancelled', 'awaiting_term', 'awaiting_delivery_term', 'awaiting_delivery_inspection', 'awaiting_return_term', 'awaiting_return_inspection'].includes(contract.status) && (
                                <button
                                  onClick={() => { setOpenActionMenu(null); navigate(`/contracts/${contract.id}/edit`) }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  <DocumentCheckIcon className="w-4 h-4 text-purple-500" /> Enviar Clicksign
                                </button>
                              )}
                              {contract.clicksign_document_key && (
                                <>
                                  <button
                                    onClick={() => { setOpenActionMenu(null); handleDownloadClicksign(contract) }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <DocumentArrowDownIcon className="w-4 h-4 text-blue-500" /> Baixar PDF Clicksign
                                  </button>
                                  {contract.clicksign_document_key && (
                                    <button
                                      onClick={() => { setOpenActionMenu(null); setResendClicksignModal(contract) }}
                                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                    >
                                      🔄 Novo Envelope Clicksign
                                    </button>
                                  )}
                                </>
                              )}
                              {['active', 'completed'].includes(contract.status) && (
                                <>
                                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                                  <button
                                    onClick={() => {
                                      setOpenActionMenu(null)
                                      setRenewForm({ mode: 'months', months: 12, days: 30, custom_date: '', new_monthly_rate: String(contract.monthly_rate || '') })
                                      setRenewModal(contract)
                                    }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                                  >
                                    🔄 Prorrogar Contrato
                                  </button>
                                </>
                              )}
                              {contract.status === 'active' && !contract.vehicle_repossessed && (
                                <>
                                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                                  <button
                                    onClick={() => { setOpenActionMenu(null); handleRepossessVehicle(contract) }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                  >
                                    🚛 Retomar Veículo
                                  </button>
                                </>
                              )}
                              {!['completed', 'finished', 'cancelled'].includes(contract.status) && (
                                <>
                                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                                  <button
                                    onClick={() => { setOpenActionMenu(null); handleDeleteContract(contract) }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <TrashIcon className="w-4 h-4" /> Cancelar Contrato
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* VISUALIZAÇÃO KANBAN COMPACTA */
        <div className="flex gap-4 overflow-x-auto pb-4">
        {Object.entries(contracts).map(([status, contractList]) => (
          <div key={status} className="min-w-72 flex-shrink-0 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {getStatusTitle(status)}
              </h3>
              <span className="badge badge-info">
                {contractList.length}
              </span>
            </div>
            
            <div className="space-y-4">
              {contractList.map((contract) => (
                <div
                  key={contract.id}
                  className={`card p-4 border-l-4 ${getStatusColor(status)} hover:shadow-md transition-shadow cursor-pointer`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getVehicleIcon(contract.vehicle_type)}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                          {contract.client_name}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Score: {contract.client_score}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-600">
                      R$ {(contract.total_amount || 0).toLocaleString()}
                    </span>
                  </div>

                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    {contract.vehicle_brand} {contract.vehicle_model} - {contract.license_plate}
                  </p>

                  <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex justify-between">
                      <span>Duração:</span>
                      <span>{contract.duration_days} dias</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Mensal:</span>
                      <span>R$ {contract.monthly_rate}</span>
                    </div>
                    {contract.days_remaining && (
                      <div className="flex justify-between">
                        <span>Restam:</span>
                        <span className={contract.days_remaining <= 7 ? 'text-red-600' : 'text-green-600'}>
                          {contract.days_remaining} dias
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {status === 'negotiating' && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>Progresso</span>
                        <span>{contract.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${contract.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Payment Progress */}
                  {(status === 'active' && contract.payment_progress !== undefined) && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>Pagamentos</span>
                        <span>{contract.payment_progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${contract.payment_progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        R$ {contract.total_paid?.toLocaleString()} recebido
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewContract(contract.id)}
                        className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded transition-colors"
                        title="Visualizar"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditContract(contract.id)}
                        className="p-1 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/50 rounded transition-colors"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {status === 'approved' && (
                        <button
                          onClick={() => handleSignContract(contract.id)}
                          className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                        >
                          📝 Assinar DocuSign
                        </button>
                      )}
                      {status === 'active' && (
                        <>
                          <button
                            onClick={() => handleViewPayments(contract.id)}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
                          >
                            💰 Pagamentos
                          </button>
                          <button
                            onClick={() => handleDeleteContract(contract)}
                            className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                            title="Cancelar contrato (Clicksign preservado)"
                          >
                            ✕ Cancelar
                          </button>
                        </>
                      )}
                      {contract.clicksign_document_key && (
                        <>
                          <button
                            onClick={() => handleDownloadClicksign(contract)}
                            className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded transition-colors"
                            title="Baixar PDF do Clicksign"
                          >
                            <DocumentArrowDownIcon className="w-4 h-4" />
                          </button>
                          {contract.status === 'sent_to_clicksign' && (
                            <>
                              <button
                                onClick={() => setResendClicksignModal(contract)}
                                className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded transition-colors"
                                title="Cancelar envelope atual e gerar novo"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {contractList.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-3xl mb-2">📋</div>
                  <p className="text-sm">Nenhum contrato neste status</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Modal de Detalhes */}
      {selectedContract && (
        <ContractDetailModal
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
          onEdit={handleEditFromModal}
          onStatusChange={(contractId, newStatus) => {
            // Atualizar o contrato na lista local sem recarregar a página
            setContracts(prev => prev.map(c =>
              c.id === contractId ? { ...c, status: newStatus } : c
            ))
          }}
        />
      )}

      {/* Modal de Assinaturas */}
      <SignaturesModal
        isOpen={signaturesModalOpen}
        onClose={() => {
          setSignaturesModalOpen(false)
          setSelectedContractIdForSignatures(null)
          setSignaturesModalType('contract')
        }}
        contractId={selectedContractIdForSignatures}
        type={signaturesModalType}
      />

      {/* Modal de Cancelamento de Contrato */}
      {cancelModalContract && (
        <CancelContractModal
          contract={cancelModalContract}
          onClose={() => setCancelModalContract(null)}
          onConfirm={handleConfirmCancelContract}
        />
      )}

      {/* Modal Termo de Entrega (Retirada) */}
      {termModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-xl">📋</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Termo de Entrega</h3>
                  <p className="text-sm text-gray-500">Contrato {termModal.contract_number} — {termModal.client_name}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Preencha os dados do representante autorizado a retirar o veículo em nome da locatária. O termo será enviado para os sócios assinarem no Clicksign.
              </p>

              <div className="space-y-3">
                {[
                  { key: 'representative_name', label: 'Nome completo', type: 'text' },
                  { key: 'representative_cpf', label: 'CPF', type: 'text' },
                  { key: 'representative_rg', label: 'RG', type: 'text' },
                  { key: 'representative_cnh', label: 'CNH nº', type: 'text' },
                  { key: 'representative_cnh_category', label: 'Categoria CNH', type: 'text' },
                  { key: 'representative_cnh_validity', label: 'Validade CNH', type: 'date' },
                  { key: 'representative_phone', label: 'Telefone', type: 'text' },
                  { key: 'representative_role', label: 'Vínculo com a Locatária', type: 'text' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{field.label}</label>
                    <input
                      type={field.type}
                      value={termForm[field.key]}
                      onChange={e => setTermForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder={field.label}
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setTermModal(null)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleSendTerm(termModal)}
                  disabled={termLoading || !termForm.representative_name || !termForm.representative_cpf}
                  className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {termLoading ? 'Enviando...' : 'Enviar para Clicksign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Termo de Devolução */}
      {returnTermModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <TruckIcon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Termo de Devolução</h3>
                  <p className="text-sm text-gray-500">Contrato {returnTermModal.contract_number} — {returnTermModal.client_name}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Preencha os dados do representante autorizado a devolver o veículo e acompanhar a vistoria de devolução. O termo será enviado para os sócios assinarem no Clicksign.
              </p>

              <div className="space-y-3">
                {[
                  { key: 'representative_name', label: 'Nome completo', type: 'text' },
                  { key: 'representative_cpf', label: 'CPF', type: 'text' },
                  { key: 'representative_rg', label: 'RG', type: 'text' },
                  { key: 'representative_cnh', label: 'CNH nº', type: 'text' },
                  { key: 'representative_cnh_category', label: 'Categoria CNH', type: 'text' },
                  { key: 'representative_cnh_validity', label: 'Validade CNH', type: 'date' },
                  { key: 'representative_phone', label: 'Telefone', type: 'text' },
                  { key: 'representative_role', label: 'Vínculo com a Locatária', type: 'text' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{field.label}</label>
                    <input
                      type={field.type}
                      value={returnTermForm[field.key]}
                      onChange={e => setReturnTermForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder={field.label}
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setReturnTermModal(null)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleSendReturnTerm(returnTermModal)}
                  disabled={returnTermLoading || !returnTermForm.representative_name || !returnTermForm.representative_cpf}
                  className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {returnTermLoading ? 'Enviando...' : 'Enviar para Clicksign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reenvio para Clicksign */}
      {resendClicksignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Novo Envelope Clicksign</h3>
                  <p className="text-sm text-gray-500">Contrato nº {resendClicksignModal.contract_number} — {resendClicksignModal.client_name}</p>
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-5">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  ⚠️ O envelope atual será <strong>cancelado</strong> no Clicksign e um novo será criado com os mesmos signatários. Todas as assinaturas pendentes serão invalidadas.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setResendClicksignModal(null)}
                  disabled={resendLoading}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleResendClicksign(resendClicksignModal)}
                  disabled={resendLoading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {resendLoading ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enviando...</>
                  ) : (
                    <>🔄 Gerar Novo Envelope</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Prorrogação de Contrato */}
      {renewModal && (() => {
        const previewDate = getRenewPreviewDate()
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">🔄 Prorrogar Contrato</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{renewModal.client_name} — {renewModal.contract_identifier}</p>
                </div>
                <button onClick={() => setRenewModal(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">✕</button>
              </div>

              <div className="p-5 space-y-4">
                {/* Prazo atual */}
                <div className="flex gap-3 text-sm">
                  <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Prazo atual</div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {fmtDate(renewModal.end_date)}
                    </div>
                    {renewModal.renewal_count > 0 && (
                      <div className="text-xs text-indigo-500 mt-0.5">Renovado {renewModal.renewal_count}x</div>
                    )}
                  </div>
                  <div className="flex items-center text-gray-400 text-xl">→</div>
                  <div className={`flex-1 rounded-lg p-3 ${previewDate ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                    <div className="text-xs text-gray-500 mb-1">Novo prazo</div>
                    <div className={`font-semibold ${previewDate ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-400'}`}>
                      {previewDate ? previewDate.toLocaleDateString('pt-BR') : '—'}
                    </div>
                  </div>
                </div>

                {/* Modo de extensão */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Prorrogar por</label>
                  <div className="flex gap-2 mb-3">
                    {[['months','Meses'],['days','Dias'],['date','Data específica']].map(([val, lbl]) => (
                      <button
                        key={val}
                        onClick={() => setRenewForm(f => ({ ...f, mode: val }))}
                        className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium border transition-colors ${renewForm.mode === val ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-indigo-400'}`}
                      >{lbl}</button>
                    ))}
                  </div>

                  {renewForm.mode === 'months' && (
                    <div className="flex items-center gap-3">
                      <input
                        type="number" min="1" max="120"
                        value={renewForm.months}
                        onChange={e => setRenewForm(f => ({ ...f, months: e.target.value }))}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">meses a partir do prazo atual</span>
                    </div>
                  )}
                  {renewForm.mode === 'days' && (
                    <div className="flex items-center gap-3">
                      <input
                        type="number" min="1" max="3650"
                        value={renewForm.days}
                        onChange={e => setRenewForm(f => ({ ...f, days: e.target.value }))}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">dias a partir do prazo atual</span>
                    </div>
                  )}
                  {renewForm.mode === 'date' && (
                    <input
                      type="date"
                      value={renewForm.custom_date}
                      min={renewModal.end_date}
                      onChange={e => setRenewForm(f => ({ ...f, custom_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  )}
                </div>

                {/* Novo valor mensal (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Valor mensal <span className="text-gray-400 font-normal">(opcional — deixe em branco para manter)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
                    <input
                      type="number" min="0" step="0.01"
                      placeholder={renewModal.monthly_rate}
                      value={renewForm.new_monthly_rate}
                      onChange={e => setRenewForm(f => ({ ...f, new_monthly_rate: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {renewModal.original_end_date && (
                  <div className="text-xs text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                    Prazo original do contrato: {fmtDate(renewModal.original_end_date)}
                  </div>
                )}
              </div>

              <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setRenewModal(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >Cancelar</button>
                <button
                  onClick={handleRenewContract}
                  disabled={renewLoading || (renewForm.mode === 'date' && !renewForm.custom_date)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {renewLoading ? 'Salvando...' : '🔄 Confirmar Prorrogação'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

    </div>
  )
}

export default ContractsPage